import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	AudioLines,
	Film,
	Image,
	type LucideIcon,
	Sparkles,
} from "lucide-react";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { Reveal } from "@/components/reveal";
import { AUDIO_MODELS } from "@/lib/audio-models";
import { IMAGE_MODELS } from "@/lib/image-models";
import { buildPageHead } from "@/lib/seo-meta";
import { VIDEO_MODELS } from "@/lib/video-models";

export const Route = createFileRoute("/models")({
	component: ModelsPage,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: "/models",
			title: "Model Library - AgentFramer",
			description:
				"Browse the image, video, and audio generation models available to AI agents through AgentFramer MCP tools.",
		}),
});

function ModelsPage() {
	return (
		<MarketingPageShell>
			<section className="homepage-hover-cell rounded-md bg-background px-6 py-12 text-center sm:px-10 sm:py-16">
				<Reveal>
					<p className="font-mono text-[11px] tracking-normal text-muted-foreground/70">
						Model library
					</p>
					<h1 className="mx-auto mt-3 max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl">
						Every media model behind one MCP interface.
					</h1>
					<p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
						Agents can generate images, videos, and audio without learning a new
						provider API for each model. AgentFramer keeps model constraints,
						defaults, and pricing metadata in one catalog.
					</p>
					<div className="mt-7 flex flex-wrap items-center justify-center gap-3">
						<Link
							to="/help/mcp-tools"
							className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
						>
							View MCP tools
							<ArrowRight className="size-4" />
						</Link>
						<Link
							to="/signup"
							className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
						>
							Connect your agent
						</Link>
					</div>
				</Reveal>
			</section>

			<ModelSection
				title="Image Models"
				description="Prompt-to-image models with verified dimensions and defaults."
				icon={Image}
				models={IMAGE_MODELS.slice(0, 9).map((model) => ({
					id: model.id,
					label: model.label,
					description: model.description,
					meta: `${model.defaultWidth}x${model.defaultHeight}`,
					isDefault: model.isDefault,
				}))}
			/>
			<ModelSection
				title="Video Models"
				description="Text and image-to-video models with duration and aspect-ratio constraints."
				icon={Film}
				models={VIDEO_MODELS.slice(0, 9).map((model) => ({
					id: model.id,
					label: model.label,
					description: model.description,
					meta: `${model.defaultDurationSeconds}s, ${model.defaultAspectRatio}`,
					isDefault: model.isDefault,
				}))}
			/>
			<ModelSection
				title="Audio Models"
				description="Music, voice, and sound generation models for agentic workflows."
				icon={AudioLines}
				models={AUDIO_MODELS.slice(0, 9).map((model) => ({
					id: model.id,
					label: model.label,
					description: model.description,
					meta: `${model.defaultDurationSeconds}s default`,
					isDefault: model.isDefault,
				}))}
			/>

			<section className="homepage-hover-cell rounded-md bg-background px-8 py-10 sm:py-12">
				<Reveal className="mx-auto max-w-2xl text-center">
					<Sparkles className="mx-auto size-5 text-[#6B3FF2]" />
					<h2 className="mt-4 text-3xl font-semibold">
						Agents can omit model IDs.
					</h2>
					<p className="mt-3 text-base leading-relaxed text-muted-foreground">
						Every tool has a recommended default. Advanced agents can override
						models when they need a specific provider, duration, size, or output
						style.
					</p>
				</Reveal>
			</section>
		</MarketingPageShell>
	);
}

function ModelSection({
	description,
	icon: Icon,
	models,
	title,
}: {
	description: string;
	icon: LucideIcon;
	models: Array<{
		description: string;
		id: string;
		isDefault: boolean;
		label: string;
		meta: string;
	}>;
	title: string;
}) {
	return (
		<section className="bg-border">
			<div className="homepage-hover-cell rounded-md bg-background">
				<Reveal className="px-8 py-10">
					<div className="flex items-start gap-4">
						<div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-card">
							<Icon className="size-5 text-[#6B3FF2]" />
						</div>
						<div>
							<h2 className="text-3xl font-semibold">{title}</h2>
							<p className="mt-2 max-w-2xl text-base leading-relaxed text-muted-foreground">
								{description}
							</p>
						</div>
					</div>
				</Reveal>
			</div>
			<div className="grid grid-cols-1 gap-px md:grid-cols-3">
				{models.map((model, index) => (
					<div
						key={model.id}
						className="homepage-hover-cell rounded-md bg-background p-6"
					>
						<Reveal delay={(index % 3) * 70}>
							<div className="flex items-start justify-between gap-3">
								<div>
									<h3 className="text-base font-semibold">{model.label}</h3>
									<p className="mt-1 font-mono text-xs text-muted-foreground">
										{model.id}
									</p>
								</div>
								{model.isDefault ? (
									<span className="rounded-full bg-[#6B3FF2]/10 px-2 py-0.5 text-[11px] font-medium text-[#6B3FF2]">
										Default
									</span>
								) : null}
							</div>
							<p className="mt-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
								{model.description}
							</p>
							<p className="mt-4 font-mono text-xs text-[#6B3FF2]">
								{model.meta}
							</p>
						</Reveal>
					</div>
				))}
			</div>
		</section>
	);
}
